import type { ReactNode } from 'react';

/**
 * Tones map to the status and accent token triplets, never to raw colours.
 *
 * `danger`/`warning`/`success`/`info` carry meaning and are reserved for it. The `accent-*`
 * tones are categorical — a source name or an action kind — and must not be used for
 * severity, or the palette stops telling the reader anything.
 */
export type Tone =
  | 'neutral'
  | 'brand'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'violet'
  | 'teal'
  | 'purple'
  | 'sky'
  | 'amber'
  | 'green';

const TONE_CLASSES: Record<Tone, string> = {
  neutral: 'bg-neutral-bg text-neutral-fg',
  brand: 'bg-brand-subtle text-brand-text',
  success: 'bg-success-bg text-success-fg',
  warning: 'bg-warning-bg text-warning-fg',
  danger: 'bg-danger-bg text-danger-fg',
  info: 'bg-info-bg text-info-fg',
  violet: 'bg-accent-violet-bg text-accent-violet-fg',
  teal: 'bg-accent-teal-bg text-accent-teal-fg',
  purple: 'bg-accent-purple-bg text-accent-purple-fg',
  sky: 'bg-accent-sky-bg text-accent-sky-fg',
  amber: 'bg-accent-amber-bg text-accent-amber-fg',
  green: 'bg-accent-green-bg text-accent-green-fg',
};

const RING_CLASSES: Record<Tone, string> = {
  neutral: 'ring-1 ring-neutral-border',
  brand: 'ring-1 ring-brand/40',
  success: 'ring-1 ring-success-border',
  warning: 'ring-1 ring-warning-border',
  danger: 'ring-1 ring-danger-border',
  info: 'ring-1 ring-info-border',
  violet: 'ring-1 ring-accent-violet-fg/30',
  teal: 'ring-1 ring-accent-teal-fg/30',
  purple: 'ring-1 ring-accent-purple-fg/30',
  sky: 'ring-1 ring-accent-sky-fg/30',
  amber: 'ring-1 ring-accent-amber-fg/30',
  green: 'ring-1 ring-accent-green-fg/30',
};

interface BadgeProps {
  tone?: Tone;
  children: ReactNode;
  /** Adds a ring, for the one badge on a row that needs to win. */
  emphasis?: boolean;
  title?: string;
  className?: string;
}

export function Badge({
  tone = 'neutral',
  children,
  emphasis = false,
  title,
  className = '',
}: BadgeProps) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${TONE_CLASSES[tone]} ${emphasis ? RING_CLASSES[tone] : ''} ${className}`}
    >
      {children}
    </span>
  );
}
